import { User } from '../types/auth';
import { authConfig } from '../config/auth';

// Cross-subdomain cookie utilities
class CrossSubdomainCookieManager {
  /**
   * Set a temporary authentication cookie that works across bellomyonline.com subdomains
   * This allows microservices to read authentication without any code changes
   */
  setCrossSubdomainAuthCookie(authentication: any): void {
    try {
      // Set a temporary authentication cookie that works across bellomyonline.com subdomains
      // This allows microservices to read authentication without any code changes
      const cookieValue = JSON.stringify({
        jwt: authentication.jwt,
        sessionID: authentication.sessionID,
        userID: authentication.userID,
        audience: this.getAudience() || "",
        timestamp: new Date().getTime()
      });

      // Set cookie with domain=.bellomyonline.com to work across subdomains
      // Max age of 5 minutes for security
      const cookieString = "SIRC_TEMP_AUTH=" + encodeURIComponent(cookieValue) +
        "; domain=.bellomyonline.com" +
        "; path=/" +
        "; secure" +
        "; max-age=300" + // 5 minutes
        "; SameSite=None";

      document.cookie = cookieString;

      console.log("Set cross-subdomain authentication cookie (expires in 5 minutes)");

      // Schedule cleanup
      setTimeout(() => {
        this.clearCrossSubdomainAuthCookie();
      }, 300000); // 5 minutes

    } catch (e) {
      console.log("Could not set cross-subdomain authentication cookie:", e);
    }
  }

  /**
   * Clear the cross-subdomain authentication cookie
   */
  clearCrossSubdomainAuthCookie(): void {
    try {
      // Clear the cookie by setting it with an expired date
      const expiredCookieString = "SIRC_TEMP_AUTH=; domain=.bellomyonline.com; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; secure; SameSite=None";
      document.cookie = expiredCookieString;
      console.log("Cleared cross-subdomain authentication cookie");
    } catch (e) {
      console.log("Could not clear cross-subdomain authentication cookie:", e);
    }
  }

  /**
   * Get authentication data from cross-subdomain cookie
   */
  getCrossSubdomainAuthCookie(): any | null {
    try {
      const cookies = document.cookie.split(';');
      for (let cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        if (name === 'SIRC_TEMP_AUTH') {
          const decodedValue = decodeURIComponent(value);
          return JSON.parse(decodedValue);
        }
      }
      return null;
    } catch (e) {
      console.log("Could not read cross-subdomain authentication cookie:", e);
      return null;
    }
  }

  /**
   * Check if cross-subdomain cookie is valid (not expired)
   */
  isCrossSubdomainCookieValid(cookieData: any): boolean {
    if (!cookieData || !cookieData.timestamp) {
      return false;
    }
    
    const now = new Date().getTime();
    const cookieAge = now - cookieData.timestamp;
    const maxAge = 300000; // 5 minutes in milliseconds
    
    return cookieAge < maxAge;
  }

  private getAudience(): string {
    // Try to get audience from various sources
    return authConfig.audience || 'BRIRC';
  }
}

class BRIAuthService {
  private token: string | null;
  private cookieManager: CrossSubdomainCookieManager;

  constructor() {
    this.token = localStorage.getItem('bri_token');
    this.cookieManager = new CrossSubdomainCookieManager();
  }

  redirectToLogin(redirectUri?: string) {
    const uri = redirectUri || `${window.location.origin}/auth/callback`;
    const params = new URLSearchParams({
      redirect_uri: uri,
      audience: authConfig.audience
    });
    window.location.href = `${authConfig.loginUrl}?${params}`;
  }

  /**
   * Check for existing authentication from cross-subdomain cookie
   * This allows seamless authentication if user is already logged in on another subdomain
   */
  checkCrossSubdomainAuth(): boolean {
    const cookieData = this.cookieManager.getCrossSubdomainAuthCookie();
    
    if (cookieData && this.cookieManager.isCrossSubdomainCookieValid(cookieData)) {
      // Use the JWT from the cookie if we don't have a local token
      if (!this.token && cookieData.jwt) {
        this.setToken(cookieData.jwt);
        console.log("Found valid cross-subdomain authentication");
        return true;
      }
    }
    
    return false;
  }

  async refreshToken(): Promise<string | null> {
    if (!this.token) return null;

    try {
      const response = await fetch(authConfig.refreshUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        this.token = data.accessToken;
        localStorage.setItem('bri_token', data.accessToken);
        return data.accessToken;
      }
    } catch (error) {
      // console.error('Token refresh failed:', error);
    }

    this.token = null;
    localStorage.removeItem('bri_token');
    this.cookieManager.clearCrossSubdomainAuthCookie();
    return null;
  }

  async getUserInfo(): Promise<User | null> {
    if (!this.token) return null;

    try {
      const response = await fetch(authConfig.verifyUrl, {
        headers: {
          'Authorization': `Bearer ${this.token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        return data.claims;
      }
    } catch (error) {
      // console.error('Failed to get user info:', error);
    }

    return null;
  }

  async logout(refreshToken?: string): Promise<void> {
    // Always call the logout API to properly invalidate sessions
    try {
      const logoutPayload: any = {};
      
      // Include refresh token if provided
      if (refreshToken) {
        logoutPayload.refresh_token = refreshToken;
      }
      
      // Always clear legacy cookies for complete logout
      logoutPayload.clear_legacy_cookies = true;
      
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      
      // Include JWT in Authorization header for legacy JWT invalidation
      if (this.token) {
        headers['Authorization'] = `Bearer ${this.token}`;
      }
      
      await fetch(authConfig.logoutUrl, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(logoutPayload)
      });
    } catch (error) {
      // console.error('Logout API error:', error);
      // Continue with local cleanup even if API call fails
    }

    // CRITICAL: Clear ALL authentication state
    this.token = null;
    localStorage.removeItem('bri_token');
    localStorage.removeItem('bri_refresh_token');
    localStorage.removeItem('bri_user_info');
    
    // Clear any session storage items
    sessionStorage.removeItem('bri_token');
    sessionStorage.removeItem('bri_refresh_token');
    sessionStorage.removeItem('bri_user_info');
    
    // Clear cross-subdomain authentication cookie
    this.cookieManager.clearCrossSubdomainAuthCookie();
  }

  getToken(): string | null {
    return this.token || localStorage.getItem('bri_token');
  }

  setToken(token: string): void {
    this.token = token;
    localStorage.setItem('bri_token', token);
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  /**
   * Get the cross-subdomain cookie manager for advanced usage
   */
  getCookieManager(): CrossSubdomainCookieManager {
    return this.cookieManager;
  }
}

export const authService = new BRIAuthService();
export default BRIAuthService;
